import ChiefComplaints from "./chiefComplaints/ChiefComplaints";
import ChiefComplaintEditor from "./chiefComplaints/ChiefComplaintEditor";
import Diagnosis from "./diagnosis/Diagnosis";
import DiagnosisEditor from "./diagnosis/DiagnosisEditor";
import Medicines from "./medicines/Medicines";
import MedicinesEditor from "./medicines/MedicinesEditor";
import LabInvestigations from "./labInvestigations/LabInvestigations";
import Allergies from "./allergies/Allergies";
import AllergyEditor from "./allergies/AllergyEditor";
import FamilyHistory from "./familyHistory/FamilyHistory";
import RelativeEditor from "./familyHistory/RelativeEditor";
import Hospitalisation from "./hospitalisation/Hospitalisation";
import HospitalisationEditor from "./hospitalisation/HospitalisationEditor";
import Anthropometric from "./anthropometric/Anthropometric";
import AnthropometricEditor from "./anthropometric/AnthropometricEditor";
import LabInvestigationEditor from "./labInvestigations/LabInvestigationEditor";
import Addictions from "./addictions/Addictions";
import AddictionEditor from "./addictions/AddictionEditor";
import FoodPreference from "./foodPreference/FoodPreference";
import DailySchedule from "./dailySchedule/DailySchedule";
import PhysicalActivity from "./physicalActivity/PhysicalActivity";
import ActivityEditor from "./physicalActivity/ActivityEditor";
import ProgressNotes from "./progressNotes/ProgressNotes";
import DrugSchedule from "./drugSchedule/DrugSchedule";
import DrugScheduleEditor from "./drugSchedule/DrugScheduleEditor";
import PatientDetails from "./patientDetails/PatientDetails";
import PatientDetailsEditor from "./patientDetails/PatientDetailsEditor";

/**
 * Section registry, keyed by the sidebar key. Each entry supplies the content
 * column and (optionally) the editor column. A `contentOnly: true` entry has no
 * editor rail — the content spans the full width (the Lifestyle cards).
 * Keys with no entry fall back to PatientShell's placeholder slots.
 */
const SECTIONS = {
  // Quick access — the feed is content-only; its composer is a portal modal.
  "progress-notes": { Content: ProgressNotes, contentOnly: true },

  // editorOnDemand: the middle-column FIELD/VALUE table stays wide
  // until the reviewer clicks a row, at which point PD_V2_EDIT opens
  // the right rail with the 5-field update form. Same shell contract
  // every other v2 editorOnDemand section uses.
  "patient-details": {
    Content: PatientDetails,
    Editor: PatientDetailsEditor,
    editorOnDemand: true,
  },

  // editorOnDemand: the editor rail only exists once the reviewer clicks
  // a parameter row in the Content table — the table takes the freed
  // width until then. Matches the CC / Dx / Medicines pattern.
  anthropometric: {
    Content: Anthropometric,
    Editor: AnthropometricEditor,
    editorOnDemand: true,
  },
  // editorOnDemand: the add form lives in the rail (2961:3685) and only
  // exists while open — the table takes the full width otherwise.
  "chief-complaints": {
    Content: ChiefComplaints,
    Editor: ChiefComplaintEditor,
    editorOnDemand: true,
  },
  // editorOnDemand: the add form lives in the rail (2961:4745).
  diagnosis: {
    Content: Diagnosis,
    Editor: DiagnosisEditor,
    editorOnDemand: true,
  },
  // Board 2964:4139 gave Medicines the full three-column treatment — the
  // floating-dialog Add moved into the editor rail.
  // On-demand rail (like Lab investigations): the table takes the third
  // column's width until a sheet opens.
  medicines: { Content: Medicines, Editor: MedicinesEditor, editorOnDemand: true },
  // editorOnDemand: the rail only exists while the add/edit form is open
  // (RAIL_OPEN/RAIL_CLOSE); until then the table takes the full width.
  "lab-investigations": {
    Content: LabInvestigations,
    Editor: LabInvestigationEditor,
    editorOnDemand: true,
  },
  // editorOnDemand: both forms live in the rail — Add (3071:3242) via the
  // header CTA, Update (3296:2854) via a row-name click. AllergyModal is no
  // longer mounted.
  allergies: {
    Content: Allergies,
    Editor: AllergyEditor,
    editorOnDemand: true,
  },
  // editorOnDemand: both forms live in the rail (3071:2760) — Add via the
  // header CTA, Edit via a relationship click. RelativeModal is gone.
  "family-history": {
    Content: FamilyHistory,
    Editor: RelativeEditor,
    editorOnDemand: true,
  },
  // editorOnDemand: BOTH the add and the update form live in the rail
  // (3098:2681) — the old update dialog is gone.
  hospitalisation: {
    Content: Hospitalisation,
    Editor: HospitalisationEditor,
    editorOnDemand: true,
  },
  // editorOnDemand: the drug-schedule rail hosts three exclusive modes
  // (Add / Update / View) driven by DS_V2_* events from the middle
  // column; the rail emits RAIL_OPEN/CLOSE alongside its mode changes
  // so PatientShell shows/hides the 340px column at the right times.
  "drug-schedule": {
    Content: DrugSchedule,
    Editor: DrugScheduleEditor,
    editorOnDemand: true,
  },

  // Lifestyle — content-only (no editor rail)
  "food-preference": { Content: FoodPreference, contentOnly: true },
  "daily-schedule": { Content: DailySchedule, contentOnly: true },
  // editorOnDemand: board 3071:4236 draws the editor as a rail CARD in the
  // third column, not a centred modal — both Add (toolbar) and Edit (row
  // click) open there.
  "physical-activity": {
    Content: PhysicalActivity,
    Editor: ActivityEditor,
    editorOnDemand: true,
  },
  // Addictions v2 has a fixed 4-entry table; the row itself opens a workspace
  // modal and the status chip opens the update modal. No editor rail.
  // editorOnDemand: board 3071:2446 draws the update dialog as a rail CARD,
  // not a centred modal. There is no add flow — the four rows are fixed —
  // so the rail only ever opens on a row/chip click.
  addictions: {
    Content: Addictions,
    Editor: AddictionEditor,
    editorOnDemand: true,
  },
};

export default SECTIONS;
